import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { locationsAPI, clientsAPI } from '../../api/client';
import { formatPhoneInput } from '../../lib/validators';
import { VENUE_TYPES, STATUS_CONFIG, MAPS_KEY } from '../../lib/locationConstants';
import { GoogleMap, Marker, useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import { ArrowLeft, MapPin, Users, Save, Navigation, Search } from 'lucide-react';
import { toast } from 'sonner';

// Google Maps script libraries — `places` is required for the search-by-name
// Autocomplete in Section 4. Declared at module scope so React doesn't
// recreate the array (which would force the loader to reload).
const MAPS_LIBRARIES = ['places'];

const MAP_CONTAINER = { width: '100%', height: '520px' };  // doubled from 260px for easier point selection
const MEXICO_CENTER = { lat: 19.4326, lng: -99.1332 };

// inputCls defined at module level to avoid recreation on each render
const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary";

// F defined at module level — CRITICAL: if defined inside the component it causes
// remounting on every render (inputs lose focus after each keystroke)
function F({ label, children, required }) {
  return (
    <div>
      <label htmlFor="f-field" className="soem-label block mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="flex-1 h-px bg-slate-200" />
      <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 whitespace-nowrap">{children}</h3>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

function GeoMap({ lat, lng, onChange }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: MAPS_KEY,
    libraries: MAPS_LIBRARIES,
  });
  const [mapInstance, setMapInstance] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const markerPos = lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng) } : null;

  useEffect(() => {
    if (!mapInstance || !markerPos) return;
    mapInstance.setCenter(markerPos);
  }, [lat, lng]);

  const handleMapClick = (e) => {
    onChange({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  };

  const handlePlaceChanged = () => {
    if (!autocomplete) return;
    const place = autocomplete.getPlace();
    if (!place?.geometry?.location) {
      toast.error('No se pudo encontrar ese lugar. Intenta otra búsqueda.');
      return;
    }
    const newLat = place.geometry.location.lat();
    const newLng = place.geometry.location.lng();
    onChange({ lat: newLat, lng: newLng });
    if (mapInstance) {
      mapInstance.setCenter({ lat: newLat, lng: newLng });
      mapInstance.setZoom(17);
    }
  };

  if (loadError) {
    return (
      <div className="w-full p-4 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700">
        <p className="font-bold">No se pudo cargar Google Maps</p>
        <p className="mt-1">
          La API key de Google Maps es inválida o no está configurada. Contacta al administrador
          para configurar <code className="bg-red-100 px-1">REACT_APP_GOOGLE_MAPS_KEY</code>.
        </p>
      </div>
    );
  }

  if (!isLoaded) return <div className="w-full h-[520px] bg-slate-100 animate-pulse rounded-sm" />;

  return (
    <div className="rounded-sm overflow-hidden border border-slate-200">
      {/* Search box (Google Places Autocomplete) */}
      <div className="relative bg-white border-b border-slate-200">
        <Autocomplete
          onLoad={(ac) => setAutocomplete(ac)}
          onPlaceChanged={handlePlaceChanged}
          options={{
            componentRestrictions: { country: ['mx'] },  // bias to Mexico; remove if you need worldwide
            fields: ['geometry', 'name', 'formatted_address'],
          }}
        >
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Busca el lugar (ej. Estadio Azteca, CDMX)"
              className="w-full pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:bg-blue-50 transition-colors"
              data-testid="location-search-input"
            />
          </div>
        </Autocomplete>
      </div>

      <GoogleMap mapContainerStyle={MAP_CONTAINER}
        center={markerPos || MEXICO_CENTER} zoom={markerPos ? 16 : 10}
        onLoad={m => setMapInstance(m)} onClick={handleMapClick}
        options={{ mapTypeControl: true, streetViewControl: false, fullscreenControl: true }}>
        {markerPos && (
          <Marker position={markerPos} draggable
            onDragEnd={e => onChange({ lat: e.latLng.lat(), lng: e.latLng.lng() })} />
        )}
      </GoogleMap>
      <p className="text-xs text-slate-400 px-2 py-1 bg-slate-50">
        Busca el lugar arriba, haz clic en el mapa o arrastra el marcador para ajustar la ubicación
      </p>
    </div>
  );
}

export default function LocationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', venue_type: 'salon', status: 'activa',
    max_capacity: '', total_sqm: '',
    access_points: { entrances: '', exits: '' },
    address: { street: '', ext_number: '', int_number: '', neighborhood: '', city: '', state: '', postal_code: '', municipality: '' },
    gps_location: { lat: '', lng: '' },
    client_id: '', notes: '',
  });

  const set = (path, value) => {
    const keys = path.split('.');
    setForm(prev => {
      const copy = { ...prev };
      if (keys.length === 1) {
        copy[keys[0]] = value;
      } else if (keys.length === 2) {
        copy[keys[0]] = { ...copy[keys[0]], [keys[1]]: value };
      }
      return copy;
    });
  };

  useEffect(() => {
    clientsAPI.list().then(r => setClients(r.data.items || [])).catch(() => {});
    if (isEdit) {
      locationsAPI.get(id).then(r => {
        const loc = r.data;
        setForm({
          name: loc.name || '',
          phone: loc.phone || '',
          venue_type: loc.venue_type || 'salon',
          status: loc.status || 'activa',
          max_capacity: loc.max_capacity || '',
          total_sqm: loc.total_sqm || '',
          access_points: {
            entrances: loc.access_points?.entrances ?? '',
            exits: loc.access_points?.exits ?? '',
          },
          address: {
            street: loc.address?.street || '',
            ext_number: loc.address?.ext_number || loc.address?.exterior_number || '',
            int_number: loc.address?.int_number || loc.address?.interior_number || '',
            neighborhood: loc.address?.neighborhood || loc.address?.colony || '',
            city: loc.address?.city || '',
            state: loc.address?.state || '',
            postal_code: loc.address?.postal_code || '',
            municipality: loc.address?.municipality || '',
          },
          gps_location: {
            lat: loc.gps_location?.lat || '',
            lng: loc.gps_location?.lng || '',
          },
          client_id: loc.client_id || '',
          notes: loc.notes || '',
        });
      }).catch(() => toast.error('Error al cargar locación'));
    }
  }, [id, isEdit]);

  const geocodeAddress = async () => {
    const addr = form.address;
    const addressStr = [addr.street, addr.ext_number, addr.neighborhood, addr.municipality, addr.city, addr.state, 'México'].filter(Boolean).join(', ');
    if (!addressStr.trim()) { toast.error('Ingresa la dirección primero'); return; }
    setGeocoding(true);
    try {
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(addressStr)}&key=${MAPS_KEY}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.status === 'OK' && data.results[0]) {
        const { lat, lng } = data.results[0].geometry.location;
        set('gps_location.lat', lat);
        set('gps_location.lng', lng);
        toast.success('Coordenadas obtenidas');
      } else {
        toast.error('No se encontraron coordenadas para esta dirección');
      }
    } catch { toast.error('Error al obtener coordenadas'); }
    finally { setGeocoding(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone || null,
        venue_type: form.venue_type,
        status: form.status,
        max_capacity: form.max_capacity ? parseInt(form.max_capacity) : null,
        total_sqm: form.total_sqm ? parseFloat(form.total_sqm) : null,
        access_points: {
          entrances: parseInt(form.access_points.entrances) || 0,
          exits: parseInt(form.access_points.exits) || 0,
        },
        address: {
          street: form.address.street || null,
          ext_number: form.address.ext_number || null,
          int_number: form.address.int_number || null,
          neighborhood: form.address.neighborhood || null,
          municipality: form.address.municipality || null,
          city: form.address.city || null,
          state: form.address.state || null,
          postal_code: form.address.postal_code || null,
        },
        gps_location: form.gps_location.lat && form.gps_location.lng
          ? { lat: parseFloat(form.gps_location.lat), lng: parseFloat(form.gps_location.lng) }
          : null,
        client_id: form.client_id || null,
        notes: form.notes || null,
      };
      if (isEdit) {
        await locationsAPI.update(id, payload);
        toast.success('Locación actualizada');
        navigate(`/dashboard/locations/${id}`);
      } else {
        const r = await locationsAPI.create(payload);
        toast.success('Locación creada');
        navigate(`/dashboard/locations/${r.data.id}`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al guardar locación');
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-fade-in" data-testid="location-form">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/dashboard/locations')} className="soem-btn-secondary p-2">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">{isEdit ? 'Editar Locación' : 'Nueva Locación'}</h1>
          <p className="text-slate-500 text-sm mt-0.5">Completa la información del recinto o espacio</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* SECCIÓN 1: Info básica */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle>1. Información Básica</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Nombre de la locación" required>
              <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} required data-testid="loc-name" />
            </F>
            <F label="Teléfono">
              <input 
                type="tel" 
                className={inputCls} 
                value={form.phone} 
                onChange={e => set('phone', formatPhoneInput(e.target.value))} 
                placeholder="10 dígitos" 
                maxLength={10}
              />
            </F>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="Tipo de espacio">
              <select className={inputCls} value={form.venue_type} onChange={e => set('venue_type', e.target.value)} data-testid="venue-type">
                {Object.entries(VENUE_TYPES).map(([k, v]) => (
                  <option key={k} value={k}>{v.emoji} {v.label}</option>
                ))}
              </select>
            </F>
            <F label="Status">
              <select className={inputCls} value={form.status} onChange={e => set('status', e.target.value)}>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </F>
          </div>
        </div>

        {/* SECCIÓN 2: Capacidad y acceso */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle>2. Capacidad y Acceso</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <F label="Aforo máximo">
              <input type="number" min="0" className={inputCls} value={form.max_capacity}
                onChange={e => set('max_capacity', e.target.value)} placeholder="0" data-testid="capacity" />
            </F>
            <F label="Metros cuadrados">
              <input type="number" min="0" className={inputCls} value={form.total_sqm}
                onChange={e => set('total_sqm', e.target.value)} placeholder="0" />
            </F>
            <F label="Entradas">
              <input type="number" min="0" className={inputCls} value={form.access_points.entrances}
                onChange={e => set('access_points.entrances', e.target.value)} placeholder="0" />
            </F>
            <F label="Salidas">
              <input type="number" min="0" className={inputCls} value={form.access_points.exits}
                onChange={e => set('access_points.exits', e.target.value)} placeholder="0" />
            </F>
          </div>
        </div>

        {/* SECCIÓN 3: Dirección */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle>3. Dirección</SectionTitle>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <F label="Calle">
                <input className={inputCls} value={form.address.street} onChange={e => set('address.street', e.target.value)} />
              </F>
            </div>
            <F label="No. Exterior">
              <input className={inputCls} value={form.address.ext_number} onChange={e => set('address.ext_number', e.target.value)} />
            </F>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <F label="Colonia">
              <input className={inputCls} value={form.address.neighborhood} onChange={e => set('address.neighborhood', e.target.value)} />
            </F>
            <F label="Código Postal">
              <input className={inputCls} maxLength={5} value={form.address.postal_code}
                onChange={e => set('address.postal_code', e.target.value)} placeholder="00000" />
            </F>
          </div>
          <div>
            <F label="Delegación / Municipio">
              <input className={inputCls} value={form.address.municipality || ''} onChange={e => set('address.municipality', e.target.value)} placeholder="Ej: Álvaro Obregón" />
            </F>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <F label="Ciudad">
              <input className={inputCls} value={form.address.city} onChange={e => set('address.city', e.target.value)} />
            </F>
            <F label="Estado">
              <input className={inputCls} value={form.address.state} onChange={e => set('address.state', e.target.value)} />
            </F>
          </div>
        </div>

        {/* SECCIÓN 4: GPS */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle>4. Ubicación GPS</SectionTitle>
          <div className="grid grid-cols-2 gap-4">
            <F label="Latitud">
              <input type="number" step="any" className={inputCls}
                value={form.gps_location.lat} onChange={e => set('gps_location.lat', e.target.value)}
                placeholder="19.4326" data-testid="lat-input" />
            </F>
            <F label="Longitud">
              <input type="number" step="any" className={inputCls}
                value={form.gps_location.lng} onChange={e => set('gps_location.lng', e.target.value)}
                placeholder="-99.1332" data-testid="lng-input" />
            </F>
          </div>
          <button type="button" onClick={geocodeAddress} disabled={geocoding}
            className="soem-btn-secondary px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60">
            <Navigation size={14} />
            {geocoding ? 'Obteniendo coordenadas...' : 'Obtener coordenadas desde la dirección'}
          </button>
          <GeoMap
            lat={form.gps_location.lat}
            lng={form.gps_location.lng}
            onChange={({ lat, lng }) => {
              set('gps_location.lat', lat);
              set('gps_location.lng', lng);
            }}
          />
        </div>

        {/* SECCIÓN 5: Cliente */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle>5. Cliente Asociado (Opcional)</SectionTitle>
          <F label="Cliente">
            <select className={inputCls} value={form.client_id} onChange={e => set('client_id', e.target.value)}>
              <option value="">— Sin cliente asociado —</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </F>
        </div>

        {/* SECCIÓN 6: Notas */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle>6. Notas Generales</SectionTitle>
          <textarea className={`${inputCls} resize-none`} rows={4}
            placeholder="Información adicional sobre la locación..."
            value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button type="submit" disabled={saving}
            className="soem-btn-primary flex-1 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="save-location-btn">
            <Save size={15} /> {saving ? 'Guardando...' : (isEdit ? 'Actualizar Locación' : 'Guardar Locación')}
          </button>
          <button type="button" onClick={() => navigate('/dashboard/locations')}
            className="soem-btn-secondary px-8 py-3 text-sm">
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}
